import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  AlertCircle,
  BriefcaseBusiness,
  Check,
  CheckCircle2,
  ChefHat,
  ChevronDown,
  Edit3,
  Eye,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  Users,
  X,
} from "lucide-react";

import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useConfirm } from "../../components/feedback/ConfirmProvider";
import userManagementApi from "../../api/userManagementApi";
import {
  canManageUsers,
  getApiMessage,
  getUserEstablishmentId,
  getUserMode,
  roleLabels,
  translateFallback,
} from "./userManagementHelpers";
import "../../styles/users/user-management-list.css";

/**
 * Custom status dropdown used by every user row.
 */
function UserStatusSelect({
  record,
  t,
  onChange,
}) {
  const selectRef = useRef(null);
  const [open, setOpen] =
    useState(false);

  const options = [
    {
      value: "active",
      label: translateFallback(
        t,
        "usersManagement.status.active",
        "Active",
      ),
    },
    {
      value: "inactive",
      label: translateFallback(
        t,
        "usersManagement.status.inactive",
        "Inactive",
      ),
    },
  ];

  const currentValue =
    record.is_active
      ? "active"
      : "inactive";

  const currentOption =
    options.find(
      (option) =>
        option.value ===
        currentValue,
    ) || options[0];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick = (
      event,
    ) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(
          event.target,
        )
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (
      event,
    ) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick,
    );

    document.addEventListener(
      "keydown",
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick,
      );

      document.removeEventListener(
        "keydown",
        handleEscape,
      );
    };
  }, [open]);

  return (
    <div
      ref={selectRef}
      className={`users-list-status-select users-list-status-select--${currentValue} ${
        open
          ? "users-list-status-select--open"
          : ""
      }`}
    >
      <button
        type="button"
        className="users-list-status-select__trigger"
        onClick={() =>
          setOpen(
            (current) => !current,
          )
        }
        aria-label={translateFallback(
          t,
          "usersManagement.table.status",
          "Status",
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>
          {currentOption.label}
        </span>

        <ChevronDown size={14} />
      </button>

      {open && (
        <div
          className="users-list-status-select__menu"
          role="listbox"
        >
          {options.map((option) => {
            const selected =
              option.value ===
              currentValue;

            return (
              <button
                key={option.value}
                type="button"
                className={`users-list-status-select__option ${
                  selected
                    ? "users-list-status-select__option--active"
                    : ""
                }`}
                onClick={() => {
                  setOpen(false);

                  onChange(
                    record,
                    option.value ===
                      "active",
                  );
                }}
                role="option"
                aria-selected={
                  selected
                }
              >
                <span>
                  {option.label}
                </span>

                {selected && (
                  <Check size={14} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UserManagement() {
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useAuth();
  const { t } = useLanguage();
  const { confirm } = useConfirm();

  const salesPage = location.pathname.startsWith("/sales-representatives");
  const mode = getUserMode(user);
  const isSalesView = salesPage && mode === "managers";

  const canAccess =
    canManageUsers(user);

  const establishmentId =
    useMemo(
      () =>
        getUserEstablishmentId(
          user,
        ),
      [user],
    );

  const [records, setRecords] =
    useState([]);

  const [meta, setMeta] =
    useState(null);

  const [search, setSearch] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  const [message, setMessage] =
    useState(null);

  const [direction, setDirection] =
    useState(
      document.documentElement.dir ||
        "ltr",
    );

  const isRtl =
    direction === "rtl";

  const activeCount =
    useMemo(() => {
      return records.filter(
        (record) =>
          Boolean(
            record.is_active,
          ),
      ).length;
    }, [records]);

  const inactiveCount =
    useMemo(() => {
      return (
        records.length -
        activeCount
      );
    }, [
      activeCount,
      records.length,
    ]);

  const pageTitle =
    isSalesView ? "Sales Representatives" : mode === "managers"
      ? translateFallback(
          t,
          "usersManagement.managers.title",
          "Managers",
        )
      : translateFallback(
          t,
          "usersManagement.staff.title",
          "Staff",
        );

  const pageDescription =
    isSalesView
      ? "Manage field-sales representatives who register restaurants, subscriptions and QR orders."
      : mode === "managers"
      ? translateFallback(
          t,
          "usersManagement.managers.description",
          "Create and manage business owner accounts.",
        )
      : translateFallback(
          t,
          "usersManagement.staff.description",
          "Create waiter and cuisini accounts for this restaurant.",
        );

  /**
   * Keep the page synchronized with the global application direction.
   */
  useEffect(() => {
    const updateDirection = () => {
      setDirection(
        document.documentElement.dir ||
          "ltr",
      );
    };

    updateDirection();

    const observer =
      new MutationObserver(
        updateDirection,
      );

    observer.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "dir",
          "lang",
        ],
      },
    );

    return () => {
      observer.disconnect();
    };
  }, []);

  /**
   * Show a temporary feedback message.
   */
  const showMessage = (
    type,
    text,
  ) => {
    setMessage({
      type,
      text,
    });

    window.setTimeout(() => {
      setMessage(null);
    }, type === "success"
      ? 2600
      : 5200);
  };

  /**
   * Load managers or establishment staff.
   */
  const loadRecords =
    async () => {
      if (!canAccess) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        if (isSalesView) {
          // Dedicated sales endpoint; do not fetch managers and filter locally.
          const response = await userManagementApi.getSalesRepresentatives({
            search: search.trim() || undefined,
            per_page: 15,
          });
          const rows = response.data.data || [];
          // Defense in depth in case the API includes unrelated roles.
          setRecords(rows.filter((record) =>
            String(record.role?.name || record.role || "")
              .toLowerCase().replace(/[\s_-]/g, "") === "salesrepresentative"
          ));
          setMeta(response.data.meta || null);
          return;
        }

        if (
          mode === "managers"
        ) {
          const response =
            await userManagementApi.getManagers(
              {
                search:
                  search.trim() ||
                  undefined,
                per_page: 15,
              },
            );

          setRecords(
            response.data.data ||
              [],
          );

          setMeta(
            response.data.meta ||
              null,
          );

          return;
        }

        const response =
          await userManagementApi.getStaff(
            establishmentId,
            {
              search:
                search.trim() ||
                undefined,
            },
          );

        setRecords(
          response.data.data || [],
        );

        setMeta(null);
      } catch (error) {
        showMessage(
          "error",
          getApiMessage(
            error,
            translateFallback(
              t,
              "usersManagement.messages.loadError",
              "Unable to load users.",
            ),
          ),
        );
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    if (
      location.state?.saved
    ) {
      showMessage(
        "success",
        translateFallback(
          t,
          "usersManagement.messages.saved",
          "User saved successfully.",
        ),
      );

      window.history.replaceState(
        {},
        document.title,
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    loadRecords();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    isSalesView,
    establishmentId,
    canAccess,
  ]);

  const filteredRecords =
    useMemo(() => {
      if (
        (mode === "managers" && !isSalesView) ||
        !search.trim()
      ) {
        return records;
      }

      const needle =
        search
          .trim()
          .toLowerCase();

      return records.filter(
        (record) => {
          return [
            record.name,
            record.email,
            record.phone,
            record.role,
          ]
            .filter(Boolean)
            .some((value) =>
              String(value)
                .toLowerCase()
                .includes(needle),
            );
        },
      );
    }, [
      mode,
      isSalesView,
      records,
      search,
    ]);

  /**
   * Update the user status to the explicitly selected value.
   */
  const updateStatus = async (
    record,
    nextStatus,
  ) => {
    if (
      Boolean(
        record.is_active,
      ) === nextStatus
    ) {
      return;
    }

    try {
      if (isSalesView) {
        await userManagementApi.updateSalesRepresentativeStatus(
          record.id,
          nextStatus,
        );
      } else if (
        mode === "managers"
      ) {
        await userManagementApi.updateManagerStatus(
          record.id,
          nextStatus,
        );
      } else {
        await userManagementApi.updateStaffStatus(
          establishmentId,
          record.id,
          nextStatus,
        );
      }

      await loadRecords();

      showMessage(
        "success",
        translateFallback(
          t,
          "usersManagement.messages.statusUpdated",
          "User status updated successfully.",
        ),
      );
    } catch (error) {
      showMessage(
        "error",
        getApiMessage(
          error,
          translateFallback(
            t,
            "usersManagement.messages.statusError",
            "Unable to update user status.",
          ),
        ),
      );
    }
  };

  /**
   * Remove a staff member after confirmation.
   */
  const removeStaff =
    async (record) => {
      const confirmed =
        await confirm({
          title:
            translateFallback(
              t,
              "common.delete",
              "Delete",
            ),

          message:
            translateFallback(
              t,
              "usersManagement.messages.confirmDelete",
              "Remove this staff user from the restaurant?",
            ),

          itemName:
            record?.name ||
            record?.email ||
            "",

          confirmText:
            translateFallback(
              t,
              "common.delete",
              "Delete",
            ),

          cancelText:
            translateFallback(
              t,
              "common.cancel",
              "Cancel",
            ),

          closeText:
            translateFallback(
              t,
              "common.close",
              "Close",
            ),

          variant: "danger",
        });

      if (!confirmed) {
        return;
      }

      try {
        await userManagementApi.deleteStaff(
          establishmentId,
          record.id,
        );

        await loadRecords();

        showMessage(
          "success",
          translateFallback(
            t,
            "usersManagement.messages.deleted",
            "Staff user removed successfully.",
          ),
        );
      } catch (error) {
        showMessage(
          "error",
          getApiMessage(
            error,
            translateFallback(
              t,
              "usersManagement.messages.deleteError",
              "Unable to remove staff user.",
            ),
          ),
        );
      }
    };

  if (!canAccess) {
    return (
      <main
        className={`users-list-page ${
          isRtl
            ? "users-list-page--rtl"
            : "users-list-page--ltr"
        }`}
        dir={direction}
      >
        <section className="users-list-blocked">
          <ShieldCheck
            size={34}
          />

          <h1>
            {translateFallback(
              t,
              "usersManagement.blocked.title",
              "Access restricted",
            )}
          </h1>

          <p>
            {translateFallback(
              t,
              "usersManagement.blocked.description",
              "Only super admin and manager accounts can manage users.",
            )}
          </p>
        </section>
      </main>
    );
  }

  return (
    <main
      className={`users-list-page ${
        isRtl
          ? "users-list-page--rtl"
          : "users-list-page--ltr"
      }`}
      dir={direction}
    >
      <section className="users-list-hero">
        <div className="users-list-hero__copy">
          <p className="users-list-eyebrow">
            {isSalesView
              ? "Add Sales Representative"
              : mode === "managers"
              ? translateFallback(
                  t,
                  "usersManagement.eyebrow.superAdmin",
                  "Super admin control",
                )
              : translateFallback(
                  t,
                  "usersManagement.eyebrow.manager",
                  "Restaurant staff control",
                )}
          </p>

          <h1>
            {pageTitle}
          </h1>

          <span>
            {pageDescription}
          </span>
        </div>

        <div className="users-list-hero__actions">
          <button
            type="button"
            className="users-list-add"
            onClick={() =>
              navigate(
                isSalesView ? "/sales-representatives/new" : "/users/new",
              )
            }
          >
            <Plus size={18} />

            {isSalesView
              ? "Add Sales Representative"
              : mode === "managers"
              ? translateFallback(
                  t,
                  "usersManagement.actions.addManager",
                  "Add manager",
                )
              : translateFallback(
                  t,
                  "usersManagement.actions.addStaff",
                  "Add staff",
                )}
          </button>
        </div>
      </section>

      <section className="users-list-stats">
        <article>
          <span>
            {translateFallback(
              t,
              "usersManagement.stats.total",
              "Total",
            )}
          </span>

          <strong>
            {records.length}
          </strong>
        </article>

        <article>
          <span>
            {translateFallback(
              t,
              "usersManagement.status.active",
              "Active",
            )}
          </span>

          <strong>
            {activeCount}
          </strong>
        </article>

        <article>
          <span>
            {translateFallback(
              t,
              "usersManagement.status.inactive",
              "Inactive",
            )}
          </span>

          <strong>
            {inactiveCount}
          </strong>
        </article>
      </section>

      {message && (
        <div
          className={`users-list-alert users-list-alert--${message.type}`}
          role="alert"
        >
          {message.type ===
          "success" ? (
            <CheckCircle2
              size={18}
            />
          ) : (
            <AlertCircle
              size={18}
            />
          )}

          <span>
            {message.text}
          </span>

          <button
            type="button"
            onClick={() =>
              setMessage(null)
            }
            aria-label={translateFallback(
              t,
              "common.close",
              "Close",
            )}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <section className="users-list-panel">
        <div className="users-list-toolbar">
          <label className="users-list-search">
            <Search size={18} />

            <input
              value={search}
              dir={direction}
              placeholder={translateFallback(
                t,
                "usersManagement.filters.searchPlaceholder",
                "Search by name, email, phone or role...",
              )}
              onChange={(
                event,
              ) =>
                setSearch(
                  event.target.value,
                )
              }
              onKeyDown={(
                event,
              ) => {
                if (
                  event.key ===
                  "Enter"
                ) {
                  loadRecords();
                }
              }}
            />
          </label>

          <button
            type="button"
            className="users-list-refresh"
            onClick={
              loadRecords
            }
          >
            <RefreshCw
              size={17}
            />

            {translateFallback(
              t,
              "usersManagement.actions.refresh",
              "Refresh",
            )}
          </button>
        </div>

        {loading ? (
          <div className="users-list-loading">
            <Loader2
              size={28}
            />

            <span>
              {translateFallback(
                t,
                "usersManagement.loading",
                "Loading users...",
              )}
            </span>
          </div>
        ) : (
          <div className="users-list-table-wrap">
            <table className="users-list-table">
              <thead>
                <tr>
                  <th>
                    {translateFallback(
                      t,
                      "usersManagement.table.user",
                      "User",
                    )}
                  </th>

                  <th>
                    {translateFallback(
                      t,
                      "usersManagement.table.role",
                      "Role",
                    )}
                  </th>

                  <th>
                    {translateFallback(
                      t,
                      "usersManagement.table.status",
                      "Status",
                    )}
                  </th>

                  <th>
                    {translateFallback(
                      t,
                      "usersManagement.table.establishment",
                      "Establishment",
                    )}
                  </th>

                  <th>
                    {translateFallback(
                      t,
                      "usersManagement.table.actions",
                      "Actions",
                    )}
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredRecords.map(
                  (record) => (
                    <tr
                      key={
                        record.id
                      }
                    >
                      <td>
                        <button
                          type="button"
                          className="users-list-user"
                          onClick={() =>
                            navigate(
                              `/users/${record.id}`,
                            )
                          }
                        >
                          <span className="users-list-user__icon">
                            <UserRound
                              size={18}
                            />
                          </span>

                          <div className="users-list-user__content">
                            <strong>
                              {record.name}
                            </strong>

                            <small dir="ltr">
                              {record.email}
                            </small>

                            {record.phone && (
                              <small dir="ltr">
                                {
                                  record.phone
                                }
                              </small>
                            )}
                          </div>
                        </button>
                      </td>

                      <td>
                        <span
                          className={`users-list-role users-list-role--${record.role}`}
                        >
                          {record.role ===
                          "cuisini" ? (
                            <ChefHat
                              size={15}
                            />
                          ) : record.role ===
                            "manager" ? (
                            <BriefcaseBusiness
                              size={15}
                            />
                          ) : (
                            <Users
                              size={15}
                            />
                          )}

                          {isSalesView ? "Sales Representative" : translateFallback(
                            t,
                            `usersManagement.roles.${record.role}`,
                            roleLabels[
                              record.role
                            ] ||
                              record.role,
                          )}
                        </span>
                      </td>

                      <td>
                        <UserStatusSelect
                          record={
                            record
                          }
                          t={t}
                          onChange={
                            updateStatus
                          }
                        />
                      </td>

                      <td>
                        {record.establishment_id ||
                          "-"}
                      </td>

                      <td>
                        <div className="users-list-actions">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                isSalesView ? `/sales-representatives/${record.id}/edit` : `/users/${record.id}`,
                              )
                            }
                            aria-label={translateFallback(
                              t,
                              "usersManagement.actions.view",
                              "View user details",
                            )}
                          >
                            <Eye
                              size={16}
                            />
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                isSalesView ? `/sales-representatives/${record.id}/edit` : `/users/${record.id}/edit`,
                              )
                            }
                            aria-label={translateFallback(
                              t,
                              "usersManagement.actions.edit",
                              "Edit user",
                            )}
                          >
                            <Edit3
                              size={16}
                            />
                          </button>

                          {mode ===
                            "staff" && (
                            <button
                              type="button"
                              className="danger"
                              onClick={() =>
                                removeStaff(
                                  record,
                                )
                              }
                              aria-label={translateFallback(
                                t,
                                "usersManagement.actions.remove",
                                "Remove staff user",
                              )}
                            >
                              <Trash2
                                size={16}
                              />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ),
                )}

                {!filteredRecords.length && (
                  <tr>
                    <td colSpan="5">
                      <div className="users-list-empty">
                        <Users
                          size={30}
                        />

                        <strong>
                          {translateFallback(
                            t,
                            "usersManagement.empty.title",
                            "No users found",
                          )}
                        </strong>

                        <span>
                          {translateFallback(
                            t,
                            "usersManagement.empty.description",
                            "Create your first user to start managing access.",
                          )}
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {meta && (
          <div className="users-list-meta">
            {translateFallback(
              t,
              "usersManagement.pagination.total",
              "Total",
            )}
            : {meta.total}
          </div>
        )}
      </section>
    </main>
  );
}

export default UserManagement;